import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '../constants';
import type { BillingPlanDto } from './billingService';
import type { AiPodChannel } from './aiPodService';

/** Responses come back either bare or wrapped in `{ data }`. */
function unwrap<T = any>(result: unknown): T {
  return ((result as { data?: T })?.data ?? result) as T;
}

export type AiPodBillingPlansResponse = {
  aiPod: { id: string; code: string; name: string };
  plans: BillingPlanDto[];
};

export type AiPodPlanDto = {
  id: string;
  planKey: string;
  billingMode: string;
  displayName: string;
  description?: string | null;
  amountMinor: number;
  currency: string;
  interval?: string | null;
  intervalCount?: number | null;
  stripePriceId?: string | null;
  pricingIntervals?: PricingInterval[] | null;
  trialDays?: number;
  isActive: boolean;
};

export type AllPlansRow = {
  id: string;
  planKey: string;
  billingMode: string;
  displayName: string;
  description?: string | null;
  amountMinor: number;
  currency: string;
  interval?: string | null;
  intervalCount?: number | null;
  isActive: boolean;
  updatedAt: string;
  pod: { id: string; name: string; code: string };
  featureCount: number;
};

export type PlanFeatureRow = {
  id: string;
  featureId: string;
  /** Billing interval the value applies to (a plan sold monthly and yearly has one of each). */
  interval: string;
  value: any;
};

export type PlanWithFeatures = {
  id: string;
  planKey: string;
  displayName: string;
  planFeatures: PlanFeatureRow[];
};

export type FeatureMeta = {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  type: 'boolean' | 'limit' | 'option';
  category: string;
  enforcedBy?: string;
  defaultValue: any;
  unit?: string | null;
  options?: Array<{ label: string; value: string }> | null;
  unlimitedAllowed?: boolean | null;
  minValue?: number | null;
  maxValue?: number | null;
  displayOrder?: number | null;
  showOnPricingPage?: boolean;
};

export type PlanFeaturesResponse = {
  plans: PlanWithFeatures[];
  features: FeatureMeta[];
};

export type PricingInterval = {
  interval: string;
  amountMinor: number;
  currency: string;
  stripePriceId?: string | null;
};

export type PodSubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'unpaid' | 'canceled' | 'incomplete';

export type PodSnapshotFeature = {
  featureId: string;
  key: string;
  name: string;
  type: 'boolean' | 'limit' | 'option';
  unit: string | null;
  hook: string;
  category: string;
  value: unknown;
};

/** What the tenant bought — frozen at purchase/plan change; super-admin edits don't alter it. */
export type PodSnapshot = {
  takenAt: string;
  pod: { id: string; code: string; name: string; version: number; channel: AiPodChannel };
  plan: {
    id: string;
    planKey: string;
    displayName: string;
    description: string | null;
    interval: string;
    amountMinor: number;
    currency: string;
    trialDays: number;
  };
  features: PodSnapshotFeature[];
};

export type PodUsage = { hook: 'voice_minutes' | 'messages'; used: number; limit: number; remaining: number | null };

export type PodSubscription = {
  id: string;
  tenantId: string;
  aiPodId: string;
  planId: string | null;
  source: 'stripe' | 'free' | 'granted';
  status: PodSubscriptionStatus;
  billingInterval: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  trialEnd: string | null;
  trialUsedAt: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  endedAt: string | null;
  pendingPlanId: string | null;
  pendingInterval: string | null;
  pendingEffectiveAt: string | null;
  pendingSnapshot: PodSnapshot | null;
  snapshot: PodSnapshot;
  workflowId: string | null;
  chatbotId: string | null;
  resourcesState: 'none' | 'active' | 'paused';
  activationStatus: 'pending' | 'running' | 'done' | 'failed';
  activationError: string | null;
  createdAt: string;
  aiPod: { id: string; name: string; code: string; channel: AiPodChannel };
  usage?: PodUsage[];
  usagePeriod?: { start: string; end: string | null };
};

export type SubscribeResult =
  | { kind: 'checkout'; checkoutUrl: string }
  | { kind: 'activated' }
  | { kind: 'changed'; effective: 'now' | 'renewal'; effectiveAt: string | null };

export type ChangePreview =
  | { kind: 'new'; free: boolean; trialDays: number; effectiveAt: null }
  | {
      kind: 'same' | 'upgrade' | 'downgrade';
      free: boolean;
      requiresCheckout: boolean;
      effectiveAt: string | null;
      current: { plan: PodSnapshot['plan']; features: PodSnapshotFeature[] };
      target: { plan: PodSnapshot['plan']; features: PodSnapshotFeature[] };
    };

export type PodInvoice = {
  id: string;
  number: string | null;
  status: string | null;
  amountPaid: number;
  amountDue: number;
  currency: string;
  created: string;
  periodStart: string | null;
  periodEnd: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  description: string | null;
};

/** The tenant can use the pod in these states. */
export const isUsableStatus = (status: string | undefined | null) => status === 'active' || status === 'trialing';
/** A subscription that still exists (not never-started or ended). */
export const isLiveStatus = (status: string | undefined | null) =>
  status === 'active' || status === 'trialing' || status === 'past_due' || status === 'unpaid';

/** Money in the plan's currency, e.g. "$29" / "€29.50". */
export function formatMoney(amountMinor: number, currency = 'usd'): string {
  const major = amountMinor / 100;
  try {
    return major.toLocaleString('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: Number.isInteger(major) ? 0 : 2,
      maximumFractionDigits: 2,
    });
  } catch {
    return `${formatMajorAmount(amountMinor)} ${currency.toUpperCase()}`;
  }
}

export type BillingCycle = 'monthly' | 'yearly';

const CYCLE_INTERVAL: Record<BillingCycle, 'month' | 'year'> = { monthly: 'month', yearly: 'year' };

/**
 * The price a plan is actually charged at for a billing cycle, or null when the plan
 * isn't sold on that cycle. Mirrors the backend's checkout price resolution so the
 * amount shown is the amount Stripe charges.
 */
export function planPriceForCycle(
  plan: BillingPlanDto,
  cycle: BillingCycle
): { interval: 'month' | 'year'; amountMinor: number; currency: string } | null {
  const interval = CYCLE_INTERVAL[cycle];
  if (plan.interval === interval) {
    return { interval, amountMinor: plan.amountMinor, currency: plan.currency };
  }
  const row = plan.pricingIntervals?.find((r) => r.interval === interval);
  return row ? { interval, amountMinor: row.amountMinor, currency: row.currency } : null;
}

/** Major-unit amount for display: whole numbers stay whole, fractions keep two decimals. */
export function formatMajorAmount(amountMinor: number): string {
  const major = amountMinor / 100;
  return Number.isInteger(major) ? String(major) : major.toFixed(2);
}

export type CreatePlanPayload = {
  planKey: string;
  billingMode: string;
  displayName: string;
  description?: string;
  // Multi-interval (preferred)
  pricingIntervals?: PricingInterval[];
  // Legacy single-interval fields
  amountMinor?: number;
  currency?: string;
  interval?: string;
  intervalCount?: number;
  stripePriceId?: string;
  stripeProductId?: string;
  trialDays?: number;
};

export type UpdatePlanPayload = Partial<Omit<CreatePlanPayload, 'planKey' | 'billingMode'>> & {
  isActive?: boolean;
  stripePriceId?: string | null;
};

export type UsageMeterDto = {
  id: string;
  aiPodId: string;
  key: string;
  label: string;
  unit: string;
  internalCostMinor: number;
  displayOrder: number;
  overagePolicy: 'ALLOW' | 'HARD_STOP' | 'NOTIFY_ONLY';
};

export type UpsertUsageMeterPayload = {
  key: string;
  label: string;
  unit: string;
  internalCostMinor: number;
  displayOrder?: number;
  overagePolicy?: 'ALLOW' | 'HARD_STOP' | 'NOTIFY_ONLY';
};

export type PlanMeterRow = {
  id: string;
  meterId: string;
  includedQty: number;
  overageMinor: number;
};

export type PlanMetersResponse = {
  plans: Array<{ id: string; planMeters: PlanMeterRow[] }>;
  meters: UsageMeterDto[];
};

export const aiPodBillingService = {
  async listAllPlans(params?: { search?: string; status?: 'active' | 'inactive' }): Promise<{ plans: AllPlansRow[] }> {
    const qs = new URLSearchParams();
    if (params?.search) qs.set('search', params.search);
    if (params?.status) qs.set('status', params.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const result = await apiClient.get<{ plans: AllPlansRow[] } | { data: { plans: AllPlansRow[] } }>(
      `${API_ENDPOINTS.BILLING.ALL_AI_POD_PLANS}${suffix}`,
      { authenticate: true }
    );
    return (result as { data?: { plans: AllPlansRow[] } }).data ?? (result as { plans: AllPlansRow[] });
  },

  /** `includeArchived` is honoured for super admins only (plan editor). */
  async listBillingPlans(aiPodId: string, opts: { includeArchived?: boolean } = {}): Promise<AiPodBillingPlansResponse> {
    const base = API_ENDPOINTS.BILLING.AI_POD_PLANS(aiPodId);
    const url = opts.includeArchived ? `${base}?includeArchived=true` : base;
    const result = await apiClient.get<AiPodBillingPlansResponse | { data: AiPodBillingPlansResponse }>(url, {
      authenticate: true,
    });
    return (result as { data?: AiPodBillingPlansResponse }).data ?? (result as AiPodBillingPlansResponse);
  },

  // ── Tenant subscriptions (tenant-level, one per pod) ──────────────────────

  async listSubscriptions(): Promise<PodSubscription[]> {
    const result = await apiClient.get(API_ENDPOINTS.BILLING.AI_POD_SUBSCRIPTIONS, { authenticate: true });
    return unwrap<{ subscriptions: PodSubscription[] }>(result).subscriptions ?? [];
  },

  /** The tenant's subscription to a pod, or null when it hasn't subscribed. */
  async getSubscription(aiPodId: string): Promise<PodSubscription | null> {
    const result = await apiClient.get(API_ENDPOINTS.BILLING.AI_POD_SUBSCRIPTION(aiPodId), { authenticate: true });
    return unwrap<{ subscription: PodSubscription | null }>(result).subscription ?? null;
  },

  /** Subscribe or change plan: returns a Stripe checkout URL, or that it was applied. */
  async subscribe(payload: {
    planId: string;
    interval?: 'month' | 'year';
    successUrl: string;
    cancelUrl: string;
  }): Promise<SubscribeResult> {
    const result = await apiClient.post(API_ENDPOINTS.BILLING.AI_POD_SUBSCRIBE, payload, { authenticate: true });
    return unwrap<SubscribeResult>(result);
  },

  async previewChange(aiPodId: string, planId: string, interval?: string): Promise<ChangePreview> {
    const qs = new URLSearchParams({ planId, ...(interval ? { interval } : {}) });
    const result = await apiClient.get(`${API_ENDPOINTS.BILLING.AI_POD_CHANGE_PREVIEW(aiPodId)}?${qs}`, { authenticate: true });
    return unwrap<ChangePreview>(result);
  },

  async cancelSubscription(aiPodId: string): Promise<{ cancelAtPeriodEnd: boolean; endsAt: string | null }> {
    const result = await apiClient.post(API_ENDPOINTS.BILLING.AI_POD_SUBSCRIPTION_CANCEL(aiPodId), {}, { authenticate: true });
    return unwrap(result);
  },

  async reactivateSubscription(aiPodId: string): Promise<{ cancelAtPeriodEnd: boolean }> {
    const result = await apiClient.post(API_ENDPOINTS.BILLING.AI_POD_SUBSCRIPTION_REACTIVATE(aiPodId), {}, { authenticate: true });
    return unwrap(result);
  },

  async cancelPendingChange(aiPodId: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.BILLING.AI_POD_SUBSCRIPTION_CANCEL_PENDING(aiPodId), {}, { authenticate: true });
  },

  async retryActivation(aiPodId: string): Promise<PodSubscription | null> {
    const result = await apiClient.post(API_ENDPOINTS.BILLING.AI_POD_SUBSCRIPTION_ACTIVATE(aiPodId), {}, { authenticate: true });
    return unwrap<PodSubscription | null>(result);
  },

  async listInvoices(aiPodId: string): Promise<{ invoices: PodInvoice[] }> {
    const result = await apiClient.get(API_ENDPOINTS.BILLING.AI_POD_INVOICES(aiPodId), { authenticate: true });
    return unwrap(result);
  },

  async openBillingPortal(returnUrl: string): Promise<{ portalUrl: string }> {
    const result = await apiClient.post(API_ENDPOINTS.BILLING.AI_POD_PORTAL_SESSION, { returnUrl }, { authenticate: true });
    return unwrap(result);
  },

  // ── Super admin: subscribers ─────────────────────────────────────────────

  async listSubscribers(aiPodId: string): Promise<PodSubscription[]> {
    const result = await apiClient.get(API_ENDPOINTS.BILLING.AI_POD_SUBSCRIBERS(aiPodId), { authenticate: true });
    return unwrap<{ subscriptions: PodSubscription[] }>(result).subscriptions ?? [];
  },

  async grantSubscription(payload: {
    tenantId: string;
    planId: string;
    interval?: 'month' | 'year';
    linkWorkflowId?: string;
    linkChatbotId?: string;
  }): Promise<PodSubscription> {
    const result = await apiClient.post(API_ENDPOINTS.BILLING.AI_POD_GRANT, payload, { authenticate: true });
    return unwrap<PodSubscription>(result);
  },

  async adminCancelSubscription(subscriptionId: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.BILLING.AI_POD_ADMIN_CANCEL(subscriptionId), {}, { authenticate: true });
  },

  async linkableResources(tenantId: string): Promise<{
    workflows: Array<{ id: string; name: string; status: string }>;
    chatbots: Array<{ id: string; name: string; isActive: boolean }>;
  }> {
    const result = await apiClient.get(API_ENDPOINTS.BILLING.AI_POD_LINKABLE(tenantId), { authenticate: true });
    return unwrap(result);
  },

  async createPlan(aiPodId: string, payload: CreatePlanPayload): Promise<AiPodPlanDto> {
    const result = await apiClient.post<AiPodPlanDto | { data: AiPodPlanDto }>(
      API_ENDPOINTS.BILLING.AI_POD_PLANS(aiPodId),
      payload,
      { authenticate: true }
    );
    return (result as { data?: AiPodPlanDto }).data ?? (result as AiPodPlanDto);
  },

  async updatePlan(aiPodId: string, planId: string, payload: UpdatePlanPayload): Promise<AiPodPlanDto> {
    const result = await apiClient.patch<AiPodPlanDto | { data: AiPodPlanDto }>(
      API_ENDPOINTS.BILLING.AI_POD_PLAN(aiPodId, planId),
      payload,
      { authenticate: true }
    );
    return (result as { data?: AiPodPlanDto }).data ?? (result as AiPodPlanDto);
  },

  async deletePlan(aiPodId: string, planId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.BILLING.AI_POD_PLAN(aiPodId, planId), { authenticate: true });
  },

  async getPlanFeatures(aiPodId: string): Promise<PlanFeaturesResponse> {
    const result = await apiClient.get<PlanFeaturesResponse | { data: PlanFeaturesResponse }>(
      API_ENDPOINTS.BILLING.AI_POD_PLAN_FEATURES(aiPodId),
      { authenticate: true }
    );
    return (result as { data?: PlanFeaturesResponse }).data ?? (result as PlanFeaturesResponse);
  },

  async getPlanFeaturesPublic(aiPodId: string): Promise<PlanFeaturesResponse> {
    const result = await apiClient.get<PlanFeaturesResponse | { data: PlanFeaturesResponse }>(
      API_ENDPOINTS.BILLING.AI_POD_PLAN_FEATURES_PUBLIC(aiPodId),
      { authenticate: true }
    );
    return (result as { data?: PlanFeaturesResponse }).data ?? (result as PlanFeaturesResponse);
  },

  async savePlanFeatures(
    aiPodId: string,
    rows: Array<{ featureId: string; planId: string; value: any }>
  ): Promise<{ saved: number }> {
    const result = await apiClient.put<{ saved: number } | { data: { saved: number } }>(
      API_ENDPOINTS.BILLING.AI_POD_PLAN_FEATURES(aiPodId),
      { rows },
      { authenticate: true }
    );
    return (result as { data?: { saved: number } }).data ?? (result as { saved: number });
  },

  async replacePlanFeatures(
    aiPodId: string,
    planId: string,
    rows: Array<{ featureId: string; interval?: string; value: any }>
  ): Promise<{ saved: number }> {
    const result = await apiClient.put<{ saved: number } | { data: { saved: number } }>(
      API_ENDPOINTS.BILLING.AI_POD_PLAN_FEATURES_REPLACE(aiPodId, planId),
      { rows },
      { authenticate: true }
    );
    return (result as { data?: { saved: number } }).data ?? (result as { saved: number });
  },

  async removeFeature(aiPodId: string, featureId: string): Promise<void> {
    await apiClient.delete(
      API_ENDPOINTS.BILLING.AI_POD_PLAN_FEATURE(aiPodId, featureId),
      { authenticate: true }
    );
  },

  async listUsageMeters(aiPodId: string): Promise<{ meters: UsageMeterDto[] }> {
    const result = await apiClient.get<{ meters: UsageMeterDto[] } | { data: { meters: UsageMeterDto[] } }>(
      API_ENDPOINTS.BILLING.AI_POD_USAGE_METERS(aiPodId),
      { authenticate: true }
    );
    return (result as { data?: { meters: UsageMeterDto[] } }).data ?? (result as { meters: UsageMeterDto[] });
  },

  async upsertUsageMeter(aiPodId: string, payload: UpsertUsageMeterPayload): Promise<UsageMeterDto> {
    const result = await apiClient.put<UsageMeterDto | { data: UsageMeterDto }>(
      API_ENDPOINTS.BILLING.AI_POD_USAGE_METERS(aiPodId),
      payload,
      { authenticate: true }
    );
    return (result as { data?: UsageMeterDto }).data ?? (result as UsageMeterDto);
  },

  async deleteUsageMeter(aiPodId: string, meterId: string): Promise<void> {
    await apiClient.delete(
      API_ENDPOINTS.BILLING.AI_POD_USAGE_METER(aiPodId, meterId),
      { authenticate: true }
    );
  },

  async getPlanMeters(aiPodId: string): Promise<PlanMetersResponse> {
    const result = await apiClient.get<PlanMetersResponse | { data: PlanMetersResponse }>(
      API_ENDPOINTS.BILLING.AI_POD_PLAN_METERS(aiPodId),
      { authenticate: true }
    );
    return (result as { data?: PlanMetersResponse }).data ?? (result as PlanMetersResponse);
  },

  async replacePlanMeters(
    aiPodId: string,
    planId: string,
    rows: Array<{ meterId: string; includedQty: number; overageMinor: number }>
  ): Promise<{ saved: number }> {
    const result = await apiClient.put<{ saved: number } | { data: { saved: number } }>(
      API_ENDPOINTS.BILLING.AI_POD_PLAN_METERS_REPLACE(aiPodId, planId),
      { rows },
      { authenticate: true }
    );
    return (result as { data?: { saved: number } }).data ?? (result as { saved: number });
  },
};

/**
 * Where "Open" goes for a subscribed pod: a chat-only pod opens its chatbot; a workflow
 * pod (or chat + workflow) opens the workflow, which offers the chatbot from there.
 */
export function podOpenPath(sub: Pick<PodSubscription, 'workflowId' | 'chatbotId'> & { aiPod?: { channel?: AiPodChannel } }): string | null {
  if (sub.workflowId) return `/workflows/${sub.workflowId}/edit`;
  if (sub.chatbotId) return `/chatbots/${sub.chatbotId}/config`;
  return null;
}

/** Plain-language status for badges. */
/** The plan limits a live subscription has used up for the current period. */
export function usedUpLimits(sub: Pick<PodSubscription, 'usage'>): PodUsage[] {
  return (sub.usage ?? []).filter((u) => u.limit > 0 && u.used >= u.limit);
}

export function podStatusLabel(sub: Pick<PodSubscription, 'status' | 'cancelAtPeriodEnd'>): string {
  if (sub.status === 'trialing') return sub.cancelAtPeriodEnd ? 'Trial · ends' : 'Free trial';
  if (sub.status === 'active') return sub.cancelAtPeriodEnd ? 'Cancelling' : 'Active';
  if (sub.status === 'past_due') return 'Payment failed';
  if (sub.status === 'unpaid') return 'Unpaid';
  if (sub.status === 'canceled') return 'Ended';
  return 'Pending';
}
